/**
 * Overlap Check Hook
 * Feature: 005-clinic-scheduling
 *
 * Real-time conflict detection for professional time ranges.
 */

import { useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth'
import type {
  OverlapCheckInput,
  OverlapCheckResult,
  OverlapConflict,
  SchedulingOverlapPolicy,
} from '../types/scheduling.types'

// ============================================================
// Overlap Check Function
// ============================================================

async function checkOverlapForProfessionals(
  organizationId: string,
  input: OverlapCheckInput,
  policy: SchedulingOverlapPolicy
): Promise<OverlapCheckResult> {
  if (policy === 'allowed') {
    return { has_conflicts: false, conflicts: [], policy }
  }

  const conflicts: OverlapConflict[] = []

  for (const professionalId of input.professional_ids) {
    // Query sessions that overlap with the proposed time range
    // Overlap condition: existing.start_time < proposed.end_time AND existing.end_time > proposed.start_time
    let query = supabase
      .from('sessions')
      .select(`
        id,
        professional_id,
        session_date,
        start_time,
        end_time,
        patient:patients (full_name),
        primary_professional:profiles!sessions_professional_id_fkey (
          id,
          full_name
        )
      `)
      .eq('organization_id', organizationId)
      .eq('session_date', input.session_date)
      .eq('professional_id', professionalId)
      .neq('status', 'cancelled')
      .lt('start_time', input.end_time)
      .gt('end_time', input.start_time)

    if (input.exclude_session_id) {
      query = query.neq('id', input.exclude_session_id)
    }

    const { data: directConflicts, error: directError } = await query

    if (directError) throw directError

    for (const conflict of directConflicts ?? []) {
      const prof = conflict.primary_professional as unknown as
        | { id: string; full_name: string }
        | null
      const pat = conflict.patient as unknown as
        | { full_name: string }
        | null

      conflicts.push({
        conflicting_session_id: conflict.id,
        professional_id: professionalId,
        professional_name: prof?.full_name ?? 'Profissional',
        patient_name: pat?.full_name ?? 'Paciente',
        session_date: conflict.session_date,
        start_time: conflict.start_time ?? '',
        end_time: conflict.end_time ?? '',
      })
    }

    // Also check session_professionals junction for additional assignments
    const additionalQuery = supabase
      .from('session_professionals')
      .select(`
        session:sessions (
          id,
          professional_id,
          session_date,
          start_time,
          end_time,
          status,
          organization_id,
          patient:patients (full_name)
        )
      `)
      .eq('professional_id', professionalId)

    const { data: additionalConflicts, error: additionalError } =
      await additionalQuery

    if (additionalError) throw additionalError

    for (const item of additionalConflicts ?? []) {
      const session = item.session as unknown as {
        id: string
        professional_id: string
        session_date: string
        start_time: string
        end_time: string
        status: string
        organization_id: string
        patient: { full_name: string } | null
      } | null
      if (!session) continue
      if (session.organization_id !== organizationId) continue
      if (session.session_date !== input.session_date) continue
      if (session.status === 'cancelled') continue
      if (
        input.exclude_session_id &&
        session.id === input.exclude_session_id
      )
        continue

      const sStart = session.start_time
      const sEnd = session.end_time
      if (sStart < input.end_time && sEnd > input.start_time) {
        // Avoid duplicate conflicts
        if (
          !conflicts.some(
            (c) => c.conflicting_session_id === session.id
          )
        ) {
          conflicts.push({
            conflicting_session_id: session.id,
            professional_id: professionalId,
            professional_name: 'Profissional',
            patient_name: session.patient?.full_name ?? 'Paciente',
            session_date: session.session_date,
            start_time: sStart,
            end_time: sEnd,
          })
        }
      }
    }
  }

  return {
    has_conflicts: conflicts.length > 0,
    conflicts,
    policy,
  }
}

// ============================================================
// Hook
// ============================================================

export function useOverlapCheck() {
  const { currentOrganization } = useAuth()
  const [isChecking, setIsChecking] = useState(false)

  const checkOverlap = useCallback(
    async (
      input: OverlapCheckInput,
      policy: SchedulingOverlapPolicy = 'allowed_with_warning'
    ): Promise<OverlapCheckResult> => {
      const organizationId = currentOrganization?.id
      if (!organizationId) {
        return { has_conflicts: false, conflicts: [], policy }
      }

      setIsChecking(true)
      try {
        return await checkOverlapForProfessionals(
          organizationId,
          input,
          policy
        )
      } finally {
        setIsChecking(false)
      }
    },
    [currentOrganization?.id]
  )

  return { checkOverlap, isChecking }
}
